import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1586348943529-beaae6c28db9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&auto=format";

function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`relative overflow-hidden inline-block align-baseline pb-[0.15em] ${className}`}>
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
        className="inline-block"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 600], ["0%", "18%"]);
  const bgScale = useTransform(scrollY, [0, 600], [1.08, 1.14]);
  const contentY = useTransform(scrollY, [0, 600], ["0%", "-6%"]);

  const scrollTo = (id) => {
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -80, duration: 1.2 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="start"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden min-h-[100svh] flex items-end pb-16 pt-36 sm:pt-40"
    >
      {/* Parallax background — z-0 base layer */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-[-5%] z-0 will-change-transform">
        <img src={HERO_IMG} alt="" aria-hidden="true" fetchPriority="high" className="w-full h-full object-cover object-center saturate-[1.15]" />
      </motion.div>
      {/* Soft cream veil over the image so text stays readable, but the foliage shows through */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#F2DFC6]/85 via-[#F2DFC6]/45 to-[#F2DFC6]/5" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-[#F2DFC6]/70" />
      <div
        className="absolute inset-0 z-[1] opacity-[0.05] mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 300 300' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <motion.div style={{ y: contentY }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#93401D] font-medium mb-8"
        >
          <Sparkles size={14} strokeWidth={1.5} />
          <span>Psychologische Beratung · Witali Albrecht</span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-serif text-[10vw] sm:text-[7.5vw] lg:text-[6.2vw] leading-[0.95] tracking-[-0.025em] text-[#29170C] max-w-5xl break-words"
        >
          <span className="block">
            <MaskLine delay={0.1}>Manchmal braucht es</MaskLine>
          </span>
          <span className="block mt-1 sm:mt-2">
            <MaskLine delay={0.25}>nur einen</MaskLine>{" "}
            <MaskLine delay={0.35}>
              <span className="italic text-[#93401D]">Menschen</span>,
            </MaskLine>
          </span>
          <span className="block mt-1 sm:mt-2">
            <MaskLine delay={0.5}>der</MaskLine>{" "}
            <MaskLine delay={0.6}>
              <span className="italic text-[#C98250]">wirklich</span>
            </MaskLine>{" "}
            <MaskLine delay={0.7}>zuhört.</MaskLine>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 text-lg sm:text-xl leading-relaxed text-[#63432B] max-w-2xl space-y-4"
        >
          <p>
            Es gibt Zeiten im Leben, in denen alles zu viel wird. Gedanken lassen
            dich nicht los, Sorgen werden größer – und manchmal weißt du einfach
            nicht mehr, wie es weitergehen soll.
          </p>
          <p className="font-serif italic text-[#29170C] text-xl sm:text-2xl">
            Du musst nicht alles alleine schaffen.
          </p>
          <p className="text-sm text-[#937353] leading-relaxed">
            Hinweis: Meine Beratung ist keine Psychotherapie und ersetzt keine
            ärztliche oder heilkundliche Behandlung.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("kontakt")}
            className="btn-primary rounded-full px-8 py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 group"
          >
            Kostenloses Erstgespräch
            <ArrowRight size={16} strokeWidth={1.8} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("ueber")}
            className="btn-ghost rounded-full px-8 py-3.5 text-sm font-medium"
          >
            Mehr über mich
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 right-5 sm:right-10 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#93401D]"
      >
        <span>Scrollen</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="w-px h-10 bg-[#93401D]"
        />
      </motion.div>
    </section>
  );
}
